import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Upload,
  FileText,
  ArrowLeft,
  Home,
  User,
  LogOut,
  CheckCircle2,
  Clock,
  XCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { value: 'aadhar', label: 'Aadhar Card' },
  { value: 'pan', label: 'PAN Card' },
  { value: 'bank_statement', label: 'Bank Statement (1 Year)' },
  { value: 'salary_slip', label: 'Salary Slip' },
  { value: 'itr', label: 'ITR (Income Tax Return)' },
  { value: 'property_docs', label: 'Property Documents' },
  { value: '7_12', label: '7/12 Extract' },
  { value: 'noc', label: 'NOC (Gram Panchayat)' },
  { value: 'photo', label: 'Passport Photo' },
  { value: 'other', label: 'Other Document' },
];

const PortalDocuments = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    document_name: '',
    document_type: '',
    file_url: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('portal_token');
    const storedUser = localStorage.getItem('portal_user');
    
    if (!token) {
      navigate('/portal/login');
      return;
    }
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    
    fetchDocuments();
  }, [navigate]);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/portal/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_user');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/portal/login');
  };

  const handleUpload = async () => {
    if (!formData.document_name || !formData.document_type || !formData.file_url) {
      toast.error('Please fill all fields');
      return;
    }
    
    setUploading(true);
    try {
      await axios.post(`${API}/portal/documents`, null, {
        params: formData
      });
      toast.success('Document uploaded successfully');
      setFormData({ document_name: '', document_type: '', file_url: '' });
      fetchDocuments();
    } catch (error) {
      toast.error('Error uploading document');
    } finally {
      setUploading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-amber-600" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/portal/dashboard" className="flex items-center gap-2 text-slate-600 hover:text-brand-navy">
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Dashboard</span>
              </Link>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg">
                <User className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">{user?.name}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-2xl font-bold text-brand-navy font-heading mb-6">
          Document Upload
        </h2>

        {/* Upload Form */}
        <Card className="border-0 shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-brand-navy">
              Upload New Document
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label>Document Name</Label>
                <Input
                  value={formData.document_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, document_name: e.target.value }))}
                  data-testid="doc-name-input"
                  placeholder="e.g., Aadhar Card - Front"
                  className="h-12"
                />
              </div>
              <div>
                <Label>Document Type</Label>
                <Select
                  value={formData.document_type}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, document_type: v }))}
                >
                  <SelectTrigger data-testid="doc-type-select" className="h-12">
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Document URL / Link</Label>
                <Input
                  value={formData.file_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, file_url: e.target.value }))}
                  data-testid="doc-url-input"
                  placeholder="Paste Google Drive / Dropbox link"
                  className="h-12"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Upload your document to Google Drive or Dropbox and paste the sharing link here
                </p>
              </div>
              <Button
                onClick={handleUpload}
                data-testid="upload-doc-btn"
                className="w-full h-12 bg-brand-navy text-white hover:bg-brand-navy/90 font-semibold"
                disabled={uploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload Document'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Required Documents Info */}
        <Card className="border-0 shadow-sm mb-8 bg-amber-50 border-l-4 border-amber-500">
          <CardContent className="p-6">
            <h3 className="font-bold text-brand-navy mb-3">Required Documents (आवश्यक कागदपत्रे)</h3>
            <ul className="text-sm text-slate-600 space-y-2">
              <li>• Aadhar Card (आधार कार्ड)</li>
              <li>• PAN Card (पॅन कार्ड)</li>
              <li>• Bank Statement - 1 Year (बँक स्टेटमेंट)</li>
              <li>• Salary Slip / ITR (सॅलरी स्लिप / आयटीआर)</li>
              <li>• Property Documents (if applicable)</li>
              <li>• 2 Passport Photos</li>
            </ul>
          </CardContent>
        </Card>

        {/* Uploaded Documents */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-brand-navy">
              Uploaded Documents ({documents.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {documents.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No documents uploaded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-100"
                  >
                    <div className="flex items-center gap-4">
                      <FileText className="h-8 w-8 text-brand-navy" />
                      <div>
                        <p className="font-medium text-brand-navy">{doc.document_name}</p>
                        <p className="text-xs text-slate-500">
                          {DOCUMENT_TYPES.find(t => t.value === doc.document_type)?.label || doc.document_type}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={
                        doc.status === 'verified' ? 'bg-emerald-100 text-emerald-700' :
                        doc.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        {getStatusIcon(doc.status)}
                        <span className="ml-1 capitalize">{doc.status}</span>
                      </Badge>
                      {doc.file_url && (
                        <a
                          href={doc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-navy hover:underline text-sm"
                        >
                          View
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PortalDocuments;
